import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  RadiologistScanDetailError,
  RadiologistScanDetailRequest,
  RadiologistScanDetailResponse,
  StudiesError,
  StudiesRequest,
  StudiesResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';
import { isNotEmpty } from '@/shared/util';

const KEY = {
  GET_RADIOLOGIST_SCAN_DETAILS: 'GET_RADIOLOGIST_SCAN_DETAILS',
  GET_STUDIES: 'GET_STUDIES',
  GET_STUDIES_BY_ID: 'GET_STUDIES_BY_ID',
} as const;

const request = {
  getRadiologistScanDetails: async (
    { payload: { studyId } }: RadiologistScanDetailRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.APPS.get<RadiologistScanDetailResponse>(
      `${ENDPOINTS.APPS.STUDY}${studyId}/radiologistScanDetails`,
      { signal },
    );

    return data;
  },
  getStudies: async ({ payload: { params } }: StudiesRequest, signal?: AbortSignal) => {
    const { data } = await fetchClient.APPS.get<StudiesResponse>(ENDPOINTS.APPS.STUDY, {
      params: removeEmptyParams(params),
      signal,
    });

    return data;
  },
};

const query = {
  useGetRadiologistScanDetails: (
    { payload }: RadiologistScanDetailRequest,
    config?: Partial<UseQueryOptions<RadiologistScanDetailResponse, RadiologistScanDetailError>>,
  ) =>
    useQuery<RadiologistScanDetailResponse, RadiologistScanDetailError>({
      queryFn: ({ signal }) => request.getRadiologistScanDetails({ payload }, signal),
      queryKey: [KEY.GET_RADIOLOGIST_SCAN_DETAILS, payload.studyId],
      ...config,
    }),
  useGetStudies: (
    { payload }: StudiesRequest,
    config?: Partial<UseQueryOptions<StudiesResponse, StudiesError>>,
  ) =>
    useQuery<StudiesResponse, StudiesError>({
      enabled: isNotEmpty(payload.params.users),
      queryFn: ({ signal }) => request.getStudies({ payload }, signal),
      queryKey: [KEY.GET_STUDIES, payload.params.users.map((user) => user), payload.params.status],
      ...config,
    }),
};

export const study = {
  KEY,
  query,
  request,
};
